import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, ArrowDown, Star, Check, ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { Reveal, MaskedLine } from '../components/Reveal';
import { PaymentModal } from '../components/PaymentModal';
import { usePageMeta } from '../components/ScrollToTop';
import { site, img, trackEvent } from '../data/site';
import { ebook, playbookBenefits, playbookModules, playbookAudiences, playbookFaqs, playbookPreviews } from '../data/content';

const usePlaybookMeta = () => {
  usePageMeta(
    'TheBigHub Digital Marketing Playbook — Learn Digital Marketing for ₦3,999',
    'You promised yourself you\u2019d learn a digital skill this year. TheBigHub Digital Marketing Playbook — practical digital marketing knowledge for ₦3,999. One-time payment via bank transfer, delivered on WhatsApp.'
  );
  useEffect(() => {
    const set = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    set('property', 'og:title', 'TheBigHub Digital Marketing Playbook — ₦3,999');
    set('property', 'og:description', 'One eBook. Practical digital marketing knowledge. A new starting point. Pay once by bank transfer and receive your eBook on WhatsApp.');
    set('property', 'og:url', 'https://www.thebighub.org/digital-marketing-playbook');
    set('property', 'og:image', 'https://www.thebighub.org/ebook-flier.jpg');
    let link = document.querySelector('link[rel="canonical"]');
    if (link) link.setAttribute('href', 'https://www.thebighub.org/digital-marketing-playbook');
    trackEvent('playbook_page_view');
  }, []);
};

const Stars = () => (
  <span className="flex gap-0.5 text-amber-400" aria-label="Five star rating">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

export default function Playbook() {
  usePlaybookMeta();
  const [payOpen, setPayOpen] = useState(false);
  const [preview, setPreview] = useState(0);
  const [faq, setFaq] = useState(0);

  const buy = (source) => {
    trackEvent('purchase_cta_click', { source });
    trackEvent('payment_popup_open');
    setPayOpen(true);
  };

  const scrollToContents = () => {
    document.getElementById('inside-ebook')?.scrollIntoView({ behavior: 'smooth' });
  };

  const Buy = ({ source, className = '' }) => (
    <button
      onClick={() => buy(source)}
      data-testid={`buy-cta-${source}`}
      className={`btn-buy ${className}`}
    >
      BUY THE EBOOK <ArrowUpRight size={18} />
    </button>
  );

  return (
    <main data-testid="playbook-page" className="bg-white">
      {/* ── 1. HERO ─────────────────────────────────────────── */}
      <section className="grain relative overflow-hidden bg-pine pt-32 sm:pt-36">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-[-10%] h-[520px] w-[520px] rounded-full bg-emerald-600/40 blur-[130px]" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 bottom-[-15%] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle_at_center,rgba(217,70,239,0.2),rgba(139,92,246,0.1)_45%,transparent_70%)] blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 sm:px-8 sm:pb-24">
          <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <Reveal>
                <div className="mb-7 inline-flex items-center gap-3 rounded-full border-2 border-white/25 px-5 py-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-goldlight" aria-hidden="true" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-goldlight sm:text-xs">
                    {site.tagline}
                  </span>
                </div>
              </Reveal>
              <h1 className="text-balance font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
                <MaskedLine delay={0.1}>You Promised Yourself You'd Learn a</MaskedLine>
                <MaskedLine delay={0.22}><em className="text-rainbow">Digital Skill</em></MaskedLine>
                <MaskedLine delay={0.34}>This Year. What's Stopping You?</MaskedLine>
              </h1>
              <Reveal delay={0.45}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-white sm:text-lg">
                  January came with plans. The months passed with excuses. But your dream of
                  learning a valuable digital skill doesn't have to remain another unfinished goal.
                  With just <strong className="text-goldlight">₦3,999</strong>, you can take your
                  first practical step into digital marketing.
                </p>
              </Reveal>
              <Reveal delay={0.6}>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Buy source="hero" />
                  <button onClick={scrollToContents} data-testid="hero-see-contents" className="btn-outline-light border-2 px-8 py-5 text-base">
                    SEE WHAT YOU'LL LEARN <ArrowDown size={16} />
                  </button>
                </div>
              </Reveal>
              <Reveal delay={0.68}>
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                  <span className="flex items-center gap-2 text-sm text-white"><Stars /> Loved by learners</span>
                  <span className="flex items-center gap-2 text-sm text-white"><Check size={15} className="text-goldlight" /> One-time payment</span>
                  <span className="flex items-center gap-2 text-sm text-white"><Check size={15} className="text-goldlight" /> Delivered on WhatsApp after payment confirmation</span>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.3} className="relative">
              <div className="relative mx-auto max-w-md" style={{ perspective: '1200px' }}>
                <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-emerald-500/30 via-sky-500/20 to-fuchsia-500/25 blur-2xl" aria-hidden="true" />
                <img
                  src={ebook.image}
                  alt={ebook.imageAlt}
                  data-testid="hero-ebook-mockup"
                  className="animate-float relative w-full rounded-2xl border border-white/20 shadow-[36px_44px_90px_-24px_rgba(0,0,0,0.75)]"
                  style={{ transform: 'rotateY(-14deg) rotateX(3deg)' }}
                  loading="eager"
                />
                <div className="glass absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl px-5 py-3.5 sm:-left-10">
                  <img src="/ebook-laptop.png" alt="Customer reading the Playbook on a laptop" className="h-11 w-11 rounded-full border-2 border-white object-cover" />
                  <div>
                    <p className="font-display text-lg font-bold text-white">₦3,999</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-goldlight">One-time · Instant access after confirmation</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 2. STORYTELLING ─────────────────────────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal className="relative order-2 lg:order-1">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border-2 border-forest" aria-hidden="true" />
              <div className="img-frame aspect-[4/3] shadow-[0_36px_80px_-32px_rgba(0,59,36,0.4)]">
                <img src="/ebook-laptop.png" alt="A customer reading TheBigHub Digital Marketing Playbook on a laptop at home" loading="lazy" />
              </div>
            </Reveal>
            <div className="order-1 lg:order-2">
              <Reveal>
                <div className="mb-5 flex items-center gap-4">
                  <span className="font-mono text-xs tracking-[0.28em] text-forest">01</span>
                  <span className="gold-rule" aria-hidden="true" />
                  <span className="eyebrow">A Word With You</span>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="text-balance font-display text-3xl font-bold leading-[1.12] text-ink sm:text-4xl lg:text-5xl">
                  The Year Is Moving. Your Goals Are Still Waiting.
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="prose-tbh mt-6">
                  <p>You said you'd learn something new.</p>
                  <p>You said you'd develop a skill that could open doors.</p>
                  <p>You said you'd stop watching opportunities pass you by.</p>
                  <p>
                    Here's the truth: you don't need to understand everything about digital
                    marketing before you begin. You need a place to start.
                  </p>
                  <p className="font-display text-xl font-bold text-forest">
                    Your next chapter won't write itself. Start learning today.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <Buy source="story" className="mt-8" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. VALUE PROPOSITION ─────────────────────────────── */}
      <section className="grain relative overflow-hidden bg-forest">
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-40 h-[460px] w-[460px] rounded-full bg-[radial-gradient(circle_at_center,rgba(217,70,239,0.18),transparent_65%)] blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <div className="mb-5 flex items-center gap-4">
              <span className="font-mono text-xs tracking-[0.28em] text-goldlight">02</span>
              <span className="h-px w-16 bg-goldlight" aria-hidden="true" />
              <span className="eyebrow text-goldlight">Why It Matters</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="max-w-3xl text-balance font-display text-3xl font-bold leading-[1.12] text-white sm:text-4xl lg:text-5xl">
              Don't Just Spend Time Online. Learn How Digital Marketing Works.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white sm:text-lg">
              Every day, businesses compete for attention online. This Playbook introduces you to the knowledge and strategies behind it.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {playbookBenefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={(i % 3) * 0.08} className="h-full">
                  <div data-testid={`benefit-card-${i + 1}`} className="card-lift h-full rounded-2xl border-2 border-white bg-white p-7">
                    <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-forest/10 text-forest">
                      <Icon size={22} strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-ink">{b.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 4. WHAT'S INSIDE ─────────────────────────────────── */}
      <section id="inside-ebook" className="scroll-mt-24 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <Reveal>
                <div className="mb-5 flex items-center gap-4">
                  <span className="font-mono text-xs tracking-[0.28em] text-forest">03</span>
                  <span className="gold-rule" aria-hidden="true" />
                  <span className="eyebrow">What's Inside the eBook</span>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="max-w-2xl text-balance font-display text-3xl font-bold leading-[1.12] text-ink sm:text-4xl lg:text-5xl">
                  Everything Starts Making Sense When You Know What to Learn.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="rounded-2xl border-2 border-forest bg-forest px-6 py-4 text-white" data-testid="modules-count">
                <p className="font-display text-3xl font-bold text-goldlight">12+</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em]">In-Demand Modules</p>
              </div>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {playbookModules.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.title} delay={(i % 4) * 0.07} className="h-full">
                  <div data-testid={`module-card-${i + 1}`} className="card-lift h-full rounded-2xl border-2 border-forest bg-forest p-6">
                    <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-goldlight">
                      <Icon size={20} strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-base font-bold leading-snug text-white">{m.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white">{m.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Buy source="contents" />
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
                Plus templates, checklists, AI prompts & step-by-step strategies
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── 5. WHO IS IT FOR ─────────────────────────────────── */}
      <section className="grain relative overflow-hidden bg-pine">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 -top-40 h-[420px] w-[420px] rounded-full bg-emerald-600/40 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <div className="mb-5 flex items-center gap-4">
              <span className="font-mono text-xs tracking-[0.28em] text-goldlight">04</span>
              <span className="h-px w-16 bg-goldlight" aria-hidden="true" />
              <span className="eyebrow text-goldlight">Who Is This eBook For?</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="max-w-3xl text-balance font-display text-3xl font-bold leading-[1.12] text-white sm:text-4xl lg:text-5xl">
              You Don't Need to Be a Tech Expert to Begin.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {playbookAudiences.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={(i % 3) * 0.08} className="h-full">
                  <div data-testid={`audience-pb-${i + 1}`} className="card-lift h-full rounded-2xl border-2 border-white bg-white p-7">
                    <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-forest text-goldlight">
                      <Icon size={21} strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-ink">{a.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink">{a.text}</p>
                  </div>
                </Reveal>
              );
            })}
            <Reveal delay={0.2} className="h-full">
              <div className="flex h-full flex-col items-start justify-center rounded-2xl border-2 border-white p-7">
                <p className="font-display text-2xl font-bold leading-snug text-white">
                  Your digital growth starts with a decision.
                </p>
                <Buy source="audience" className="mt-6" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 6. PREVIEW ───────────────────────────────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <div className="mb-5 flex items-center gap-4">
              <span className="font-mono text-xs tracking-[0.28em] text-forest">05</span>
              <span className="gold-rule" aria-hidden="true" />
              <span className="eyebrow">eBook Preview</span>
            </div>
          </Reveal>
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
              <Reveal delay={0.08}>
                <h2 className="text-balance font-display text-3xl font-bold leading-[1.12] text-ink sm:text-4xl lg:text-5xl">
                  Don't Just Take Our Word for It. Look Inside.
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
                  Swipe through selected pages from the Playbook — want a free sample page first? Ask us on WhatsApp.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <Buy source="preview" className="mt-8" />
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <div className="relative" data-testid="preview-carousel">
                <div className="img-frame mx-auto aspect-square max-w-md border-2 border-forest shadow-[0_36px_80px_-32px_rgba(0,59,36,0.45)]">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={preview}
                      src={playbookPreviews[preview].image}
                      alt={`${playbookPreviews[preview].label} — TheBigHub Digital Marketing Playbook preview`}
                      initial={{ opacity: 0, x: 40 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -40 }}
                      transition={{ duration: 0.35 }}
                      className="h-full w-full object-cover"
                    />
                  </AnimatePresence>
                </div>
                {playbookPreviews.length > 1 && (
                  <div className="mt-5 flex items-center justify-center gap-4">
                    <button
                      onClick={() => setPreview((p) => (p - 1 + playbookPreviews.length) % playbookPreviews.length)}
                      data-testid="preview-prev"
                      aria-label="Previous preview page"
                      className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-forest text-forest transition-colors hover:bg-forest hover:text-white"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <span className="font-mono text-xs text-ink" data-testid="preview-counter">
                      {preview + 1} / {playbookPreviews.length} · {playbookPreviews[preview].label}
                    </span>
                    <button
                      onClick={() => setPreview((p) => (p + 1) % playbookPreviews.length)}
                      data-testid="preview-next"
                      aria-label="Next preview page"
                      className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-forest text-forest transition-colors hover:bg-forest hover:text-white"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                )}
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
                  {playbookPreviews[preview].label}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 7. PRICE & OFFER ─────────────────────────────────── */}
      <section className="grain relative overflow-hidden bg-forest">
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 top-0 h-[460px] w-[460px] rounded-full bg-[radial-gradient(circle_at_center,rgba(217,70,239,0.2),transparent_65%)] blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-5 py-20 text-center sm:px-8 sm:py-28">
          <Reveal>
            <p className="eyebrow mb-5 text-goldlight">One Small Step. A New Learning Journey.</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mx-auto max-w-3xl text-balance font-display text-3xl font-bold leading-[1.12] text-white sm:text-4xl lg:text-5xl">
              You Don't Have to Spend a Fortune to Start Learning.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white sm:text-lg">
              Your introduction to digital marketing can begin with a practical resource designed
              to help you understand the field and explore its possibilities.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mx-auto mt-10 max-w-md rounded-3xl border-2 border-white bg-white p-8 sm:p-10" data-testid="price-card">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-forest">TheBigHub Digital Marketing Playbook</p>
              <p className="mt-3 font-display text-6xl font-bold text-forest sm:text-7xl" data-testid="price-value">₦3,999</p>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-ink">One-Time Payment</p>
              <ul className="mx-auto mt-7 max-w-xs space-y-2.5 text-left">
                {[
                  'Downloadable digital eBook',
                  'Self-paced reading',
                  'Practical digital marketing knowledge',
                  'Read on a phone, laptop or tablet',
                  'Access to the purchased PDF after payment confirmation',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-ink">
                    <Check size={15} className="mt-0.5 shrink-0 text-forest" strokeWidth={3} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => buy('price')}
                data-testid="buy-cta-price"
                className="btn-buy mt-8 w-full"
              >
                BUY THE EBOOK <ArrowUpRight size={18} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── 8. TRUST ─────────────────────────────────────────── */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <div>
              <Reveal>
                <div className="mb-5 flex items-center gap-4">
                  <span className="font-mono text-xs tracking-[0.28em] text-forest">06</span>
                  <span className="gold-rule" aria-hidden="true" />
                  <span className="eyebrow">Why TheBigHub</span>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="text-balance font-display text-3xl font-bold leading-[1.12] text-ink sm:text-4xl lg:text-5xl">
                  Learn From a Team That Works in Digital Marketing.
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
                  TheBigHub is a digital marketing agency helping businesses grow through Google
                  Ads, SEO, social media management, content creation, digital advertising and
                  digital training. This Playbook distils the practical knowledge we apply for real
                  clients — into a resource you can learn from.
                </p>
              </Reveal>
              <Reveal delay={0.22}>
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {['Google Ads', 'SEO', 'Social Media Management', 'Content Creation', 'Digital Advertising', 'Digital Training'].map((s) => (
                    <span key={s} className="rounded-full border-2 border-forest px-4 py-2 text-xs font-semibold text-forest">
                      {s}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {[
                { n: '376', l: 'Booking appointments generated for Smile 360 Dental Clinic through SEO & Google Ads' },
                { n: '1,313', l: 'Admission registrations driven for IAUE across two Facebook Ads sessions' },
              ].map((s, i) => (
                <Reveal key={s.n} delay={i * 0.1} className="h-full">
                  <div className="card-lift h-full rounded-2xl border-2 border-forest bg-forest p-7" data-testid={`trust-stat-${i + 1}`}>
                    <p className="font-display text-4xl font-bold text-goldlight">{s.n}</p>
                    <p className="mt-3 text-sm leading-relaxed text-white">{s.l}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.2} className="sm:col-span-2">
                <div className="img-frame aspect-[16/8] border-2 border-forest">
                  <img src={img('photo-1600880292203-757bb62b4baf')} alt="TheBigHub strategists reviewing a client campaign" loading="lazy" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. FAQ ───────────────────────────────────────────── */}
      <section className="bg-white pb-4">
        <div className="mx-auto max-w-3xl px-5 pb-20 sm:px-8 sm:pb-28">
          <Reveal>
            <div className="mb-5 flex items-center gap-4">
              <span className="font-mono text-xs tracking-[0.28em] text-forest">07</span>
              <span className="gold-rule" aria-hidden="true" />
              <span className="eyebrow">Questions, Answered</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-balance font-display text-3xl font-bold leading-[1.12] text-ink sm:text-4xl">
              Frequently Asked Questions.
            </h2>
          </Reveal>
          <div className="mt-12 space-y-3" data-testid="faq-list">
            {playbookFaqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="overflow-hidden rounded-2xl border-2 border-forest">
                  <button
                    onClick={() => setFaq(faq === i ? -1 : i)}
                    data-testid={`faq-question-${i + 1}`}
                    aria-expanded={faq === i}
                    className="flex w-full items-center justify-between gap-4 bg-white px-6 py-5 text-left"
                  >
                    <span className="font-display text-base font-bold text-ink sm:text-lg">{f.q}</span>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-forest text-white">
                      {faq === i ? <Minus size={15} /> : <Plus size={15} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {faq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <p className="bg-forest px-6 py-5 text-sm leading-relaxed text-white" data-testid={`faq-answer-${i + 1}`}>
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 10. FINAL CTA ────────────────────────────────────── */}
      <section className="grain relative overflow-hidden bg-pine">
        <div aria-hidden="true" className="pointer-events-none absolute -left-32 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full bg-emerald-500/30 blur-[110px]" />
        <div className="relative mx-auto max-w-4xl px-5 py-20 text-center sm:px-8 sm:py-24">
          <Reveal>
            <h2 className="text-balance font-display text-3xl font-bold leading-[1.1] text-white sm:text-5xl">
              Stop Postponing. <span className="text-rainbow">Start Learning.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white sm:text-lg">
              Turn your interest into knowledge — get the Playbook for just ₦3,999.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <button
              onClick={() => buy('final')}
              data-testid="buy-cta-final"
              className="btn-buy mt-9"
            >
              BUY THE EBOOK <ArrowUpRight size={18} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* Sticky mobile purchase bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t-2 border-forest bg-white/95 p-3 backdrop-blur-lg lg:hidden" data-testid="sticky-mobile-cta">
        <button
          onClick={() => buy('sticky')}
          className="btn-buy w-full py-4 text-base"
          data-testid="buy-cta-sticky"
        >
          BUY THE EBOOK <ArrowUpRight size={18} />
        </button>
      </div>
      <div className="h-16 lg:hidden" aria-hidden="true" />

      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} />
    </main>
  );
}
